import * as React from 'react';

import {Subheader} from 'shells/typography/subheader';

import type {
    SessionUser as SessionUserFragment,
    InstanceInfo as InstanceInfoFragment,
    Lead as LeadFragment,
} from 'nutshell-graphql-types';

import {GetInstanceInfo} from '../../core/get-instance-info';
import {GetSessionUser} from '../../core/get-session-user';

import {DefaultPipelineEditCTA} from '../shared/components';

import {AutomatedEmailsContainer} from './automated-emails/automated-emails-container';
import {GetLead} from './get-lead';
import {StageGuidanceAndGoals} from './goals/stage-guidance-and-goals';
import {StagesCarouselContainer} from './stages/stages-carousel-container';

import styles from './lead-react-container.module.css';

type TemporarilySelectedStageProps = {
    temporarilySelectedStageId?: string | null | undefined;
    onTemporarilySelectStage?: (stageId: string) => void;
    onClearTemporaryStageSelection?: () => void;
};

type OwnProps = {
    id: string;
    hideNonCarouselContent?: boolean;
    isWidget?: boolean;
    // Optional controlled state props
    tempStage?: TemporarilySelectedStageProps;
};

type GraphQLProps = {
    user: SessionUserFragment | null | undefined;
    sessionInstance: InstanceInfoFragment | null | undefined;
    lead: LeadFragment | null | undefined;
    onRefetchLead: () => any;
    errorMessage: string | null | undefined;
};

type Props = {
    hideNonCarouselContent?: boolean;
    isWidget: boolean;
    // Optional controlled state props
    tempStage?: TemporarilySelectedStageProps;
} & GraphQLProps;

type State = {
    // Used to indicate which stage is being "viewed", but not actually active.
    // This happens when the user clicks on a non-active stage before confirming
    // they'd like to change stages.
    //
    // Note: This is unused if tempStage prop is provided.
    temporarilySelectedStageId: string | null | undefined;
};

export class LeadReactContainerComponent extends React.Component<Props, State> {
    constructor() {
        // @ts-expect-error
        super();

        this.state = {
            temporarilySelectedStageId: null,
        };
    }

    componentDidMount() {
        if (typeof Lead !== 'undefined' && Lead.events) {
            $(window).on(
                Lead.events.LeadBackboneModelDidUpdate,
                this.refetchGraphQLLeadAfterBackboneModelChange
            );
            $(window).on('entityWasEdited', this.refetchGraphQLLeadAfterBackboneModelChange);
        }
    }

    componentWillUnmount() {
        if (typeof Lead !== 'undefined' && Lead.events) {
            $(window).off(
                Lead.events.LeadBackboneModelDidUpdate,
                this.refetchGraphQLLeadAfterBackboneModelChange
            );
            $(window).off('entityWasEdited', this.refetchGraphQLLeadAfterBackboneModelChange);
        }
    }

    /**
     * Returns true if the component is in controlled mode (external state management)
     */
    isControlled() {
        return this.props.tempStage !== undefined;
    }

    /**
     * Gets the current temporarily selected stage ID, either from props (controlled) or state (uncontrolled)
     */
    getCurrentTemporarilySelectedStageId() {
        return this.isControlled()
            ? this.props.tempStage && this.props.tempStage.temporarilySelectedStageId
            : this.state.temporarilySelectedStageId;
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const previousActiveStageId = getActiveStageId(this.props.lead);
        const nextActiveStageId = getActiveStageId(nextProps.lead);

        if (previousActiveStageId !== nextActiveStageId) {
            // Clear our temporarily selected stageId when a new
            // stage becomes "active"
            if (this.isControlled()) {
                // In controlled mode, call the external callback
                if (this.props.tempStage && this.props.tempStage.onClearTemporaryStageSelection) {
                    this.props.tempStage.onClearTemporaryStageSelection();
                }
                // Hook the call to refresh the visible steps to run after the graphql calls
                // are finished fetching to ensure we are looking at the correct stage.
                Nut.modules.Process.showStepsOfSelectedStage();
            } else {
                // In uncontrolled mode, use internal state
                this.setState({temporarilySelectedStageId: null}, () => {
                    // Hook the call to refresh the visible steps to run after the graphql calls
                    // are finished fetching to ensure we are looking at the correct stage.
                    Nut.modules.Process.showStepsOfSelectedStage();
                });
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const prevTemporarilySelectedId = this.isControlled()
            ? prevProps.tempStage && prevProps.tempStage.temporarilySelectedStageId
            : prevState.temporarilySelectedStageId;
        const currentTemporarilySelectedId = this.getCurrentTemporarilySelectedStageId();

        if (currentTemporarilySelectedId !== prevTemporarilySelectedId) {
            // Update our phtml to show the steps of the stage that we've selected,
            // since this is done outside of our react ecosystem.
            //
            // This is updated via the classname of the div we create in react -
            // extra jank and fragile, but what we're rollin' with for now.
            Nut.modules.Process.showStepsOfSelectedStage();
        }
    }

    // Hooking into our Backbone models changes so we can properly refetch
    // this version of the lead.
    //
    // @see js/modules/core/lead.js
    refetchGraphQLLeadAfterBackboneModelChange = () => {
        this.props.onRefetchLead();
    };

    render() {
        if (this.props.errorMessage) {
            return (
                <div className={styles['lead-react-container']}>
                    <Subheader>{this.props.errorMessage}</Subheader>
                </div>
            );
        }

        const {lead, sessionInstance, hideNonCarouselContent} = this.props;

        if (lead) {
            const currentlyShownStage = this.getCurrentlyShownLeadStageEdge(lead);

            return (
                <div className='stages-carousel-wrapper'>
                    <StagesCarouselContainer
                        onTemporarilySelectStage={this.handleTemporarilySelectStage}
                        onClearTemporaryStageSelection={this.handleClearTemporaryStageSelection}
                        onRefetchLead={this.props.onRefetchLead}
                        temporarilySelectedStageId={this.getCurrentTemporarilySelectedStageId()}
                        canAccessSetup={Boolean(
                            this.props.user && this.props.user.permissions.canAccessSetup
                        )}
                        lead={lead}
                        isWidget={this.props.isWidget || false}
                    />
                    {!hideNonCarouselContent && (
                        <DefaultPipelineEditCTA
                            completedWizardKey='defaultStagesetOnboardingWizardCTAOnLeadPage'
                            dismissKey='has_dismissed_stageset_wizard_cta_lead_page_NUT-8926'
                        />
                    )}
                    {!hideNonCarouselContent &&
                    NutClientConfig.hasEmailSequencesSalesAutomation &&
                    currentlyShownStage &&
                    sessionInstance &&
                    sessionInstance.hasConfiguredNutshellEmailAutomations ? (
                        <AutomatedEmailsContainer
                            user={this.props.user}
                            currentLeadStageEdge={currentlyShownStage}
                            refetchLead={this.props.onRefetchLead}
                        />
                    ) : undefined}
                    {!hideNonCarouselContent &&
                    currentlyShownStage &&
                    this.props.user &&
                    lead.stageset &&
                    NutClientConfig.hasSalesProcessSetupOverhaulNUT7581 ? (
                        <StageGuidanceAndGoals
                            user={this.props.user}
                            stageset={lead.stageset}
                            leadStageEdge={currentlyShownStage}
                        />
                    ) : undefined}
                </div>
            );
        }

        return null;
    }

    handleTemporarilySelectStage = (stageId: string) => {
        if (this.isControlled()) {
            // In controlled mode, call the external callback
            if (this.props.tempStage && this.props.tempStage.onTemporarilySelectStage) {
                this.props.tempStage.onTemporarilySelectStage(stageId);
            }
        } else {
            // In uncontrolled mode, use internal state
            this.setState({temporarilySelectedStageId: stageId});
        }
    };

    handleClearTemporaryStageSelection = () => {
        if (this.isControlled()) {
            // In controlled mode, call the external callback
            if (this.props.tempStage && this.props.tempStage.onClearTemporaryStageSelection) {
                this.props.tempStage.onClearTemporaryStageSelection();
            }
        } else {
            // In uncontrolled mode, use internal state
            this.setState({temporarilySelectedStageId: null});
        }
    };

    /**
     * Gets the currently displayed LeadStageEdge in the UI. This is usually
     * the active stage for the lead, but if the user has clicked on another
     * stage to temporary view it, that stage is now the "currently shown"
     * stage.
     *
     * @param  {Object} lead       - Full Lead object
     * @return {Object}            - Currently shown stage (if there is one)
     */
    getCurrentlyShownLeadStageEdge(lead: LeadFragment) {
        const temporarilySelectedStageId = this.getCurrentTemporarilySelectedStageId();

        if (temporarilySelectedStageId) {
            return lead.stages.edges.find((stageEdge) => {
                return stageEdge.node.id === temporarilySelectedStageId;
            });
        }

        return lead.stages.edges.find((stageEdge) => {
            return stageEdge.isActive;
        });
    }
}

const getActiveStageId = (lead?: LeadFragment | null) => {
    if (!lead) {
        return null;
    }

    return lead.stages.current ? lead.stages.current.id : null;
};

export const LeadReactContainer = (props: OwnProps) => (
    <GetSessionUser>
        {({user: sessionUser}) => (
            <GetInstanceInfo>
                {({sessionInstance}) => (
                    <GetLead leadId={props.id}>
                        {({errorMessage, lead, refetchLead}) => {
                            return (
                                <LeadReactContainerComponent
                                    lead={lead}
                                    sessionInstance={sessionInstance}
                                    user={sessionUser}
                                    errorMessage={errorMessage}
                                    onRefetchLead={refetchLead}
                                    hideNonCarouselContent={props.hideNonCarouselContent}
                                    isWidget={props.isWidget || false}
                                    tempStage={props.tempStage}
                                />
                            );
                        }}
                    </GetLead>
                )}
            </GetInstanceInfo>
        )}
    </GetSessionUser>
);
